import React, { useEffect, useRef, useState } from 'react';
import { BTN } from '../constants/buttons';
import { MODAL_OVERLAY, MODAL_PANEL_MOBILE_CAP } from '../constants/overlayLayers';

/**
 * Shared "Clone" modal — prompts for a new name before deep-copying an RA
 * or duplicating a template. Used by both:
 *  - Library page (Templates tab) — clone template
 *  - Overview page — clone existing RA
 *
 * Props:
 *  - open: bool
 *  - title: modal heading (e.g. "Clone template", "Clone as new RA")
 *  - label: field label (e.g. "New template name", "New RA title")
 *  - defaultValue: prefilled name (typically `<source> — Copy`)
 *  - confirmLabel: button text (defaults to "Clone")
 *  - busy: bool
 *  - onCancel(): close without action
 *  - onConfirm(name): submit with the entered name
 */
export default function RaCloneModal({
  open,
  title = 'Clone',
  label = 'Name',
  defaultValue = '',
  confirmLabel = 'Clone',
  busy = false,
  onCancel,
  onConfirm,
}) {
  const [value, setValue] = useState(defaultValue);
  const inputRef = useRef(null);

  useEffect(() => {
    if (open) {
      setValue(defaultValue);
      // Defer focus so the input exists by then; select all so the user can
      // immediately retype if they want.
      setTimeout(() => {
        inputRef.current?.focus();
        inputRef.current?.select();
      }, 0);
    }
  }, [open, defaultValue]);

  if (!open) return null;

  const submit = (e) => {
    e.preventDefault();
    const trimmed = value.trim();
    if (!trimmed || busy) return;
    onConfirm?.(trimmed);
  };

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 ${MODAL_OVERLAY}`}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onCancel?.(); }}
      role="dialog"
      aria-modal="true"
    >
      <form
        onSubmit={submit}
        className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-md mx-4 p-5 ${MODAL_PANEL_MOBILE_CAP}`}
      >
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-3">{title}</h2>
        <label className="block">
          <span className="block text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400 mb-1">
            {label}
          </span>
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            disabled={busy}
            maxLength={200}
            required
            className="block w-full px-3 py-2 rounded border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-900 text-sm text-gray-900 dark:text-gray-100 focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500"
          />
        </label>
        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className={BTN.secondary}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={busy || !value.trim()}
            className={BTN.primary}
          >
            {busy ? 'Working…' : confirmLabel}
          </button>
        </div>
      </form>
    </div>
  );
}
